import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Leaf, Download, ArrowLeft, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";

const PROD_URL = "https://cabinet.capsulepatrimoine.com";
const PUBLIC_URL = typeof window !== "undefined" ? window.location.origin : "";

export default function Install() {
  const [copied, setCopied] = useState(false);
  const [useProd, setUseProd] = useState(true);
  const qrRef = useRef(null);

  // Par défaut le QR pointe vers la version PRODUCTION (stable).
  // L'utilisateur peut basculer en preview pour tester en interne.
  const installUrl = useProd ? PROD_URL : PUBLIC_URL;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier.");
    }
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const size = 1024;
    canvas.width = size;
    canvas.height = size + 120;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      ctx.fillStyle = "#7B8C7A";
      ctx.font = "bold 36px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Mon Cabinet Bien-être", size / 2, size + 60);
      ctx.fillStyle = "#6b7770";
      ctx.font = "24px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.fillText("Scannez pour accéder à l'application", size / 2, size + 100);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = "MonCabinet-QRCode.png";
      a.click();
      toast.success("QR Code téléchargé");
    };
    img.src = url;
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Mon Cabinet Bien-être", text: "Accédez à votre application :", url: installUrl }); }
      catch (e) { /* ignored */ }
    } else {
      copyUrl();
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg)" }} data-testid="install-page">
      <nav className="px-6 sm:px-10 py-5 max-w-[1200px] mx-auto">
        <Link to="/" className="flex items-center gap-3" data-testid="install-back">
          <ArrowLeft className="w-4 h-4 text-muted-soft" />
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-sage flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" strokeWidth={1.6} />
            </div>
            <div className="font-display text-sm">Mon Cabinet Bien-être</div>
          </div>
        </Link>
      </nav>

      <main className="px-6 sm:px-10 max-w-[900px] mx-auto pb-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-light mb-4">
            <span className="text-xs text-sage font-medium">📱 Installer sur smartphone</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-4">Scannez. Installez. C'est tout.</h1>
          <p className="text-muted-soft max-w-2xl mx-auto">
            Scannez ce QR Code avec votre iPhone ou Samsung pour accéder à votre application
            et l'installer sur votre écran d'accueil en 1 minute.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* QR Code card */}
          <div className="card-soft p-6 sm:p-10 text-center" data-testid="qr-card">
            <div ref={qrRef} className="bg-white inline-block p-6 rounded-2xl">
              <QRCodeSVG
                value={installUrl}
                size={260}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#3A352D"
                imageSettings={{
                  src: `${PUBLIC_URL}/icon-192.png`,
                  height: 50,
                  width: 50,
                  excavate: true,
                }}
              />
            </div>
            <div className="text-xs text-muted-soft mt-4 break-all">{installUrl}</div>

            <div className="flex items-center justify-center gap-3 mt-3 text-xs">
              <button
                onClick={() => setUseProd(true)}
                className={`px-3 py-1 rounded-full transition-all ${useProd ? "bg-sage text-white" : "text-muted-soft hover:bg-sage-light"}`}
                data-testid="qr-mode-prod"
              >
                Production (stable)
              </button>
              <button
                onClick={() => setUseProd(false)}
                className={`px-3 py-1 rounded-full transition-all ${!useProd ? "bg-sage text-white" : "text-muted-soft hover:bg-sage-light"}`}
                data-testid="qr-mode-preview"
              >
                Preview (test interne)
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-6 justify-center">
              <button onClick={downloadQR} className="btn-sage text-sm" data-testid="qr-download">
                <Download className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
                Télécharger le QR Code
              </button>
              <button onClick={copyUrl} className="btn-ghost text-sm" data-testid="qr-copy">
                {copied ? <Check className="inline w-4 h-4 mr-1.5 text-sage" /> : <Copy className="inline w-4 h-4 mr-1.5" />}
                {copied ? "Copié !" : "Copier le lien"}
              </button>
              <button onClick={shareNative} className="btn-ghost text-sm" data-testid="qr-share">
                <Share2 className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
                Partager
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-4">
            <div className="card-soft p-6" data-testid="instructions-iphone">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🍎</span>
                <div className="font-display text-lg">Sur iPhone (Safari)</div>
              </div>
              <ol className="space-y-2.5 text-sm">
                <Step n="1">Ouvrez l'<b>appareil photo</b> de votre iPhone</Step>
                <Step n="2">Visez le QR Code → une notification apparaît en haut</Step>
                <Step n="3">Tapez sur la notification → Safari s'ouvre</Step>
                <Step n="4">En bas, tapez <b>↑ Partager</b> → <b>« Sur l'écran d'accueil »</b></Step>
                <Step n="5">L'icône <span className="text-sage">🌿</span> apparaît sur votre écran d'accueil ✨</Step>
              </ol>
            </div>

            <div className="card-soft p-6" data-testid="instructions-android">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🤖</span>
                <div className="font-display text-lg">Sur Android (Chrome)</div>
              </div>
              <ol className="space-y-2.5 text-sm">
                <Step n="1">Ouvrez l'<b>appareil photo</b> ou <b>Google Lens</b></Step>
                <Step n="2">Visez le QR Code → une notification apparaît</Step>
                <Step n="3">Tapez sur la notification → Chrome s'ouvre</Step>
                <Step n="4">Menu <b>⋮</b> en haut à droite → <b>« Installer l'application »</b></Step>
                <Step n="5">L'icône <span className="text-sage">🌿</span> apparaît sur votre écran d'accueil ✨</Step>
              </ol>
            </div>

            <div className="card-soft p-5" style={{ backgroundColor: "var(--sage-light)" }}>
              <div className="text-sm font-medium mb-1">💡 Astuce pro</div>
              <div className="text-xs text-muted-soft leading-relaxed">
                Installez l'app sur votre propre smartphone pour consulter votre agenda et vos clients
                partout. Vous pouvez aussi partager ce QR Code à d'autres thérapeutes qui veulent découvrir
                Mon Cabinet Bien-être (carte de visite, signature email, groupes pro).
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-5 h-5 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-0.5 text-white text-[11px] font-medium">{n}</span>
      <span className="text-sm">{children}</span>
    </li>
  );
}
